import { useRef, useEffect, useMemo } from "react";
import * as THREE from "three";

/*  ─────────────────────────────────────────
    ScrollBackground3D — Neighbourhood Plan
    Visible buildings, roads, trees, connections
    ───────────────────────────────────────── */

export default function ScrollBackground3D() {
  const containerRef = useRef(null);
  const stateRef = useRef({ scrollProgress: 0, mouse: { x: 0, y: 0 }, frame: null });

  const canvasCheck = useMemo(() => {
    try {
      const c = document.createElement("canvas");
      return !!(c.getContext("webgl2") || c.getContext("webgl"));
    } catch { return false; }
  }, []);

  useEffect(() => {
    if (!canvasCheck || !containerRef.current) return;

    const container = containerRef.current;
    let w = window.innerWidth;
    let h = window.innerHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#0D1B2A");
    scene.fog = new THREE.Fog("#0D1B2A", 20, 42);

    const camera = new THREE.PerspectiveCamera(36, w / h, 0.1, 120);
    camera.position.set(14, 11, 14);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    container.appendChild(renderer.domElement);

    /* ── Lights — brighter for visibility ── */
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.5);
    dirLight.position.set(10, 18, 8);
    scene.add(dirLight);
    const accentLight = new THREE.PointLight(0xE07A5F, 0.5, 22);
    accentLight.position.set(0, 8, 0);
    scene.add(accentLight);
    const cielLight = new THREE.PointLight(0x98C1D9, 0.3, 16);
    cielLight.position.set(-6, 4, -6);
    scene.add(cielLight);

    const world = new THREE.Group();
    scene.add(world);

    let seed = 77;
    const rand = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };

    const disposables = [];

    /* ── Ground ── */
    const groundGeo = new THREE.PlaneGeometry(44, 44);
    const groundMat = new THREE.MeshStandardMaterial({ color: "#0F0F1A", roughness: 0.9 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.02;
    world.add(ground);
    disposables.push(groundGeo, groundMat);

    /* ── Roads ── */
    const roadMat = new THREE.MeshStandardMaterial({ color: "#1A2332", roughness: 0.7 });
    const markingMat = new THREE.MeshStandardMaterial({ color: "#415A77", roughness: 0.4, emissive: "#0D1B2A", emissiveIntensity: 0.3 });
    disposables.push(roadMat, markingMat);

    const hRoads = [-7, -2, 3, 8];
    const vRoads = [-8, -3, 2, 7];

    const makeRoad = (isH, pos) => {
      const roadW = 0.9;
      const geo = isH
        ? new THREE.BoxGeometry(36, 0.03, roadW)
        : new THREE.BoxGeometry(roadW, 0.03, 36);
      const mesh = new THREE.Mesh(geo, roadMat);
      mesh.position.set(isH ? 0 : pos, 0.015, isH ? pos : 0);
      world.add(mesh);
      disposables.push(geo);

      // Dashed center line
      for (let t = -16; t <= 16; t += 1.6) {
        const dGeo = isH
          ? new THREE.BoxGeometry(0.5, 0.035, 0.05)
          : new THREE.BoxGeometry(0.05, 0.035, 0.5);
        const dash = new THREE.Mesh(dGeo, markingMat);
        dash.position.set(isH ? t : pos, 0.035, isH ? pos : t);
        world.add(dash);
        disposables.push(dGeo);
      }

      // Sidewalk edges
      [-1, 1].forEach((side) => {
        const eGeo = isH
          ? new THREE.BoxGeometry(36, 0.06, 0.06)
          : new THREE.BoxGeometry(0.06, 0.06, 36);
        const eMat = new THREE.MeshStandardMaterial({ color: "#1a2838", roughness: 0.6 });
        const edge = new THREE.Mesh(eGeo, eMat);
        const offset = side * (roadW / 2 + 0.03);
        edge.position.set(isH ? 0 : pos, 0.03, isH ? pos + offset : offset);
        if (!isH) edge.position.set(pos + offset, 0.03, 0);
        world.add(edge);
        disposables.push(eGeo, eMat);
      });
    };

    hRoads.forEach((z) => makeRoad(true, z));
    vRoads.forEach((x) => makeRoad(false, x));

    /* ── Buildings — higher opacity, more visible ── */
    const buildings = [];
    const buildingMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color("#5A9AFF"),
      transparent: true, roughness: 0.08, metalness: 0.25, side: THREE.DoubleSide,
    });
    disposables.push(buildingMat);

    // Block centers between roads
    const bCx = [], bCz = [];
    for (let i = 0; i < vRoads.length - 1; i++) bCx.push((vRoads[i] + vRoads[i + 1]) / 2);
    bCx.push(vRoads[vRoads.length - 1] + 2.8);
    bCx.unshift(vRoads[0] - 2.8);
    for (let i = 0; i < hRoads.length - 1; i++) bCz.push((hRoads[i] + hRoads[i + 1]) / 2);
    bCz.push(hRoads[hRoads.length - 1] + 2.8);
    bCz.unshift(hRoads[0] - 2.8);

    bCx.forEach((cx) => {
      bCz.forEach((cz) => {
        const count = 1 + Math.floor(rand() * 3);
        for (let i = 0; i < count; i++) {
          const maxH = 0.8 + rand() * 4.5;
          const bw = 0.4 + rand() * 0.9;
          const bd = 0.4 + rand() * 0.9;
          // Much higher opacity for visibility
          const opacity = 0.12 + rand() * 0.2;
          const riseDelay = rand() * 0.45;
          const floatSpeed = 0.15 + rand() * 0.2;
          const floatPhase = rand() * Math.PI * 2;
          const px = cx + (rand() - 0.5) * 1.8;
          const pz = cz + (rand() - 0.5) * 1.8;

          const mat = buildingMat.clone();
          mat.opacity = 0;
          const geo = new THREE.BoxGeometry(bw, maxH, bd);
          geo.translate(0, maxH / 2, 0);
          const mesh = new THREE.Mesh(geo, mat);
          mesh.position.set(px, 0, pz);
          mesh.scale.y = 0.001;
          world.add(mesh);
          buildings.push({ mesh, mat, px, pz, maxH, targetOpacity: opacity, riseDelay, floatSpeed, floatPhase });
          disposables.push(geo, mat);
        }
      });
    });

    /* ── Trees — clearly visible green ── */
    const trees = [];
    const trunkMat = new THREE.MeshStandardMaterial({ color: "#2a1f14", roughness: 0.8 });
    const canopyColor = new THREE.Color("#1a4a2a");
    disposables.push(trunkMat);

    const addTree = (px, pz, delay) => {
      const treeH = 0.5 + rand() * 1.0;
      const canopyR = 0.3 + rand() * 0.4;

      // Trunk — thicker
      const trunkGeo = new THREE.CylinderGeometry(0.045, 0.06, treeH, 6);
      trunkGeo.translate(0, treeH / 2, 0);
      const trunk = new THREE.Mesh(trunkGeo, trunkMat);
      trunk.position.set(px, 0, pz);
      trunk.scale.y = 0.001;
      world.add(trunk);

      // Canopy — green, visible
      const cMat = new THREE.MeshStandardMaterial({
        color: canopyColor,
        transparent: true,
        opacity: 0,
        roughness: 0.6,
      });
      const canopyGeo = new THREE.IcosahedronGeometry(canopyR, 1);
      const canopy = new THREE.Mesh(canopyGeo, cMat);
      canopy.position.set(px, treeH + canopyR * 0.5, pz);
      canopy.scale.set(0.001, 0.001, 0.001);
      world.add(canopy);

      trees.push({ trunk, canopy, cMat, treeH, canopyR, riseDelay: delay });
      disposables.push(trunkGeo, canopyGeo, cMat);
    };

    // Trees along horizontal roads (both sides)
    hRoads.forEach((z) => {
      for (let x = -14; x <= 14; x += 1.2 + rand() * 1.5) {
        if (rand() > 0.35) continue;
        const side = rand() > 0.5 ? 0.8 : -0.8;
        addTree(x + (rand() - 0.5) * 0.3, z + side, rand() * 0.35);
      }
    });

    // Trees along vertical roads
    vRoads.forEach((x) => {
      for (let z = -14; z <= 14; z += 1.2 + rand() * 1.5) {
        if (rand() > 0.35) continue;
        const side = rand() > 0.5 ? 0.8 : -0.8;
        addTree(x + side, z + (rand() - 0.5) * 0.3, rand() * 0.35);
      }
    });

    /* ── Connections ── */
    const connections = [];
    const lineMat = new THREE.LineBasicMaterial({ color: 0x4A90FF, transparent: true, opacity: 0 });
    disposables.push(lineMat);

    for (let i = 0; i < buildings.length; i++) {
      for (let j = i + 1; j < buildings.length; j++) {
        const dx = buildings[i].px - buildings[j].px;
        const dz = buildings[i].pz - buildings[j].pz;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < 3.5 && dist > 1.0 && rand() > 0.55) {
          const geo = new THREE.BufferGeometry();
          const positions = new Float32Array(6);
          geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
          const m = lineMat.clone();
          const line = new THREE.Line(geo, m);
          world.add(line);
          connections.push({ line, mat: m, geo, iA: i, iB: j, connectDelay: 0.2 + rand() * 0.4, targetOpacity: 0.08 + rand() * 0.12 });
          disposables.push(geo, m);
        }
      }
    }

    /* ── Events ── */
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      stateRef.current.scrollProgress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    const onMouseMove = (e) => {
      stateRef.current.mouse.x = (e.clientX / w) * 2 - 1;
      stateRef.current.mouse.y = (e.clientY / h) * 2 - 1;
    };
    window.addEventListener("mousemove", onMouseMove);

    const camStart = new THREE.Vector3(14, 11, 14);
    const camEnd = new THREE.Vector3(7, 5.5, 7);
    const lookTarget = new THREE.Vector3(0, 0.5, 0);
    const targetRot = { x: 0, y: 0 };

    /* ── Animate ── */
    const clock = new THREE.Clock();

    const animate = () => {
      stateRef.current.frame = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const sp = stateRef.current.scrollProgress;
      const mouse = stateRef.current.mouse;

      const introP = Math.min(t / 2.5, 1);
      const scrollP = Math.min(sp / 0.35, 1);
      const rise = Math.min(introP * 0.75 + scrollP * 0.25, 1);

      // Buildings
      for (const b of buildings) {
        const lp = Math.max(0, Math.min((rise - b.riseDelay * 0.4) / (1 - b.riseDelay * 0.4), 1));
        const e = 1 - Math.pow(1 - lp, 3);
        b.mesh.scale.y = 0.001 + e * 0.999;
        b.mat.opacity = b.targetOpacity * e;
        if (e > 0.4) b.mesh.position.y = Math.sin(t * b.floatSpeed + b.floatPhase) * 0.04 * ((e - 0.4) / 0.6);
      }

      // Trees
      for (const tr of trees) {
        const lp = Math.max(0, Math.min((rise - tr.riseDelay * 0.4) / (1 - tr.riseDelay * 0.4), 1));
        const e = 1 - Math.pow(1 - lp, 3);
        tr.trunk.scale.y = 0.001 + e * 0.999;
        tr.canopy.scale.set(e, e, e);
        tr.cMat.opacity = 0.35 * e;
        tr.canopy.position.y = tr.treeH * e + tr.canopyR * 0.5 * e;
      }

      // Connections
      for (const c of connections) {
        const bA = buildings[c.iA];
        const bB = buildings[c.iB];
        const lp = Math.max(0, Math.min((rise - c.connectDelay * 0.35) / (1 - c.connectDelay * 0.35), 1));
        const e = 1 - Math.pow(1 - lp, 3);
        c.mat.opacity = c.targetOpacity * e;
        const hA = bA.maxH * bA.mesh.scale.y + bA.mesh.position.y;
        const hB = bB.maxH * bB.mesh.scale.y + bB.mesh.position.y;
        const pos = c.geo.attributes.position.array;
        pos[0] = bA.px; pos[1] = hA * 0.65; pos[2] = bA.pz;
        pos[3] = bB.px; pos[4] = hB * 0.65; pos[5] = bB.pz;
        c.geo.attributes.position.needsUpdate = true;
      }

      // Camera
      const cx = camStart.x + (camEnd.x - camStart.x) * sp + mouse.x * 0.7;
      const cy = camStart.y + (camEnd.y - camStart.y) * sp + mouse.y * 0.25;
      const cz = camStart.z + (camEnd.z - camStart.z) * sp + mouse.x * 0.35;
      camera.position.set(cx, cy, cz);
      camera.lookAt(lookTarget);

      targetRot.y = mouse.x * 0.05;
      targetRot.x = mouse.y * 0.015;
      world.rotation.y += (targetRot.y - world.rotation.y) * 0.012;
      world.rotation.x += (targetRot.x - world.rotation.x) * 0.012;

      accentLight.intensity = 0.3 + rise * 0.4;

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      w = window.innerWidth; h = window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", onResize);
      if (stateRef.current.frame) cancelAnimationFrame(stateRef.current.frame);
      if (renderer.domElement && container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      renderer.dispose();
      disposables.forEach((d) => d.dispose());
    };
  }, [canvasCheck]);

  if (!canvasCheck) {
    return <div className="fixed inset-0 z-0" style={{ background: "radial-gradient(ellipse at 50% 60%, #0A1525 0%, #0D1B2A 70%)" }} />;
  }

  return <div ref={containerRef} className="fixed inset-0 z-0" data-testid="scroll-3d-background" />;
}
